'use client';

import {
  FieldControl,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLabelRow,
  Field as FieldRoot,
} from '@savvy-kit/components/field';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { cn } from '@savvy-kit/lib/utils';
import { DatePicker } from '@savvy-kit/patterns/date-picker';
import { InfoIcon } from 'lucide-react';

/**
 * Form-ready date field: the date analog of the InputField pattern. Reads its anatomy from
 * `components/field`, so the label, description, error and their aria wiring are defined in one place
 * rather than here.
 *
 * Form-library-agnostic — drive it with `value` / `onChange` / `error` from
 * react-hook-form or plain state. For a react-hook-form field, prefer `patterns/form-field`.
 */
export type DateFieldProps = {
  /** Stable id; associates the label with the control (and the error/description via aria). */
  readonly id: string;
  /** Currently selected date. `undefined` renders the placeholder. */
  readonly value: Date | undefined;
  /** Called when the user picks (or clears) a date. */
  readonly onChange: (date: Date | undefined) => void;
  /**
   * Render a keyable text input instead of the calendar-only trigger. Typing is
   * `MM/DD/YYYY`-only and ignores `displayFormat` — don't enable it with a
   * non-`MM/dd/yyyy` `displayFormat` (e.g. `MMM yyyy`); file a task for other formats.
   */
  readonly allowTyping?: boolean;
  readonly size?: 'sm' | 'default';
  readonly label?: string;
  /** Muted helper text below the control. Stays visible while an error message is shown. */
  readonly description?: string;
  /** Puts the field in the error state (destructive trigger border + `aria-invalid`). */
  readonly error?: boolean;
  /** Message shown below the control when `error` is true. Announced via `aria-live="polite"`. */
  readonly errorMessage?: string;
  /** Appends a `*` to the label and sets `aria-required` on the control. */
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly showInfoIcon?: boolean;
  readonly infoTooltipMessage?: string;
  readonly placeholder?: string;
  /** Earliest selectable date (also constrains the year dropdown). */
  readonly minDate?: Date;
  /** Latest selectable date (also constrains the year dropdown). Set to today for a date-of-birth field. */
  readonly maxDate?: Date;
  /** Month shown when the picker opens with no value set. */
  readonly defaultMonth?: Date;
  /** date-fns format string for the trigger display. Defaults to `MM/dd/yyyy`. */
  readonly displayFormat?: string;
  /** Extra classes for the trigger button, e.g. `h-14` to match a taller sibling control. Tailwind-merged over the defaults. */
  readonly triggerClassName?: string;
  /** Classes for the calendar popover container. Overrides the default `w-96 p-0` (e.g. a viewport-relative width on mobile). */
  readonly popoverContentClassName?: string;
  /** Classes for the inner calendar. Overrides the default `w-96 [--cell-size:…]` (e.g. a compact, responsive cell size). */
  readonly calendarClassName?: string;
  /**
   * Classes for the calendar popover's positioner. Defaults to `z-[1400]` so the
   * calendar renders above MUI modal backdrops (`z-index: 1300`) — the common case,
   * since form fields usually live in dialogs. Override for other stacking contexts.
   */
  readonly popoverPositionerClassName?: string;
  /** Renders a Clear button in the popover when a date is set. Use for optional fields; omit for required ones. */
  readonly clearable?: boolean;
  /** Called when the picker closes — wire to the form library's blur/touched handler. */
  readonly onBlur?: () => void;
};

export function DateField({
  id,
  value,
  onChange,
  allowTyping,
  size = 'default',
  label,
  description,
  error,
  errorMessage,
  required,
  disabled,
  showInfoIcon,
  infoTooltipMessage,
  placeholder,
  minDate,
  maxDate,
  defaultMonth,
  displayFormat,
  triggerClassName,
  popoverContentClassName,
  calendarClassName,
  popoverPositionerClassName = 'z-[1400]',
  clearable,
  onBlur,
}: DateFieldProps) {
  const showInfoTooltip = showInfoIcon && infoTooltipMessage;

  return (
    <FieldRoot invalid={error} disabled={disabled}>
      {label ? (
        <FieldLabelRow>
          <FieldLabel required={required} className={cn(size === 'sm' && 'text-xs')}>
            {label}
          </FieldLabel>
          {showInfoTooltip ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <button type="button" aria-label={infoTooltipMessage} className="text-secondary">
                    <InfoIcon aria-hidden className="size-3" />
                  </button>
                }
              />
              <TooltipContent>
                <p>{infoTooltipMessage}</p>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </FieldLabelRow>
      ) : null}

      {/*
        `DatePicker` has a closed prop surface: it forwards `ariaDescribedby` / `ariaInvalid` /
        `ariaRequired` to its trigger rather than accepting `aria-*` directly, so the slot takes the
        function form and maps the wiring across.
      */}
      <FieldControl id={id} required={required} disabled={disabled}>
        {(slot) => (
          <DatePicker
            // The caller's own `id`, not `slot.id`: base-ui hands back a generated id on the first
            // commit, so a consumer effect keying off the id (focus, scroll-into-view) finds nothing.
            id={id}
            value={value}
            onChange={onChange}
            allowTyping={allowTyping}
            onBlur={onBlur}
            clearable={clearable}
            disabled={disabled}
            placeholder={placeholder}
            minDate={minDate}
            maxDate={maxDate}
            defaultMonth={defaultMonth}
            displayFormat={displayFormat}
            ariaLabel={label ? undefined : (placeholder ?? 'Select date')}
            ariaDescribedby={slot['aria-describedby']}
            ariaInvalid={error}
            ariaRequired={required}
            popoverContentClassName={popoverContentClassName}
            calendarClassName={calendarClassName}
            popoverPositionerClassName={popoverPositionerClassName}
            triggerClassName={cn(
              size === 'sm' && 'h-8 text-xs',
              error && 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20',
              triggerClassName,
            )}
          />
        )}
      </FieldControl>

      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <FieldError error={error} errorMessage={errorMessage} />
    </FieldRoot>
  );
}
